import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as types from '../../constants/actionTypes';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import DrawerV6 from '../Common/Drawer/DrawerV6/DrawerV6';
import Icon from '../Common/Icon';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../Common/Notification';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { actions as baselineTaskActions } from '@Actions/baselineTaskActions';

export class OrderTasksDrawer extends Component {
	constructor(props, context) {
		super(props, context);

		this.state = {
			isFormValid: true,
			isLoading: false,
			tasks: [],
		};
	}

	async componentDidMount() {
		await this.loadFilters();
	}

	loadFilters = async () => {
		try {
			this.setState({ isLoading: true });
			const request = {
				subscrptionId: this.props.subscriptionId,
				baselineId: this.props.baselineId,
				targetDaysOut: this.props.targetDaysOut,
				targetStaticDate: this.props.targetStaticDate,
				isActive: true,
			};
			const response = await this.props.baselineTaskActions.loadOrderTasksFilters(request);
			if (response.type === types.LOAD_ORDER_BASELINE_TASKS_FILTERS_SUCCESS) {
				this.setState({
					isLoading: false,
					tasks: response.payload.baselineTasks,
				});
			} else if (response.type === types.LOAD_ORDER_BASELINE_TASKS_FILTERS_FAILURE) {
				Notification('error', 'An Error Occurred', response.payload.message);
				this.setState({ isLoading: false });
			}
		} catch (error) {
			Notification('error', 'Error', error);
			this.setState({ isLoading: false });
		}
	};

	onDragEnd = (result) => {
		// dropped outside the step list area
		if (!result.destination) {
			return;
		}

		const tasks = this.state.tasks.slice();
		const [removed] = tasks.splice(result.source.index, 1);
		tasks.splice(result.destination.index, 0, removed);

		/**
		 * Always update the entire list, so the sameDayOrdinal is always 0 - n
		 * We don't know if they're all zero because v4 has known bugs with negatives and other weirdness.
		 * Hopefully this ensures any reordering is done properly with 0 - n values all the time.
		 */
		for (let i = 0; i < tasks.length; i++) {
			tasks[i].sameDayOrdinal = i;
		}
		this.setState({ tasks: tasks });
	};

	onSave = async () => {
		try {
			this.setState({ isLoading: true });
			const request = {
				subscrptionId: this.props.subscriptionId,
				baselineId: this.props.baselineId,
				targetDaysOut: this.props.targetDaysOut,
				targetStaticDate: this.props.targetStaticDate,
				baselineTasks: this.state.tasks,
			};
			const response = await this.props.baselineTaskActions.orderBaselineTasks(request);
			if (response.type === types.ORDER_BASELINE_TASKS_SUCCESS) {
				Notification('success', 'Success', 'Baseline Task order saved successfully');
				this.props.onComplete();
			} else if (response.type === types.ORDER_BASELINE_TASKS_FAILURE) {
				Notification('error', 'An Error Occurred', response.payload.message);
				this.setState({ isLoading: false });
			}
		} catch (error) {
			Notification('error', 'Error', error);
			this.setState({ isLoading: false });
		}
	};

	render() {
		const ui = {
			brand: '#6860E4',
			panel: 'white',
			panelDrag: '#F7F5FF',
			border: '#E9E7FF',
			borderActive: '#CFCBFF',
		};

		// tried moving this to scss but it just gets overridden from within the component
		const getItemStyle = (isDragging, draggableStyle) => ({
			userSelect: 'none',
			lineHeight: 1.7,
			margin: '0 0 8px 0',
			background: ui.surface,
			border: `1px solid ${isDragging ? ui.borderActive : ui.border}`,
			borderRadius: 14,
			padding: 12,
			boxShadow: isDragging ? `${ui.shadowActive}, 0 0 0 3px ${ui.brandRing}` : ui.shadow,
			transform: isDragging ? 'scale(1.005)' : 'none',
			transition: 'background .15s ease, border-color .15s ease, box-shadow .15s ease, transform .06s ease',

			...draggableStyle,
		});

		const getListStyle = (isDraggingOver) => ({
			background: isDraggingOver ? ui.panelDrag : ui.panel,
			borderRadius: '0.25rem',
			padding: 8,
			transition: 'background .2s ease, border-color .2s ease',
			height: '100%',
			maxHeight: '80vh',
			overflowY: 'auto',
			minHeight: '200px',
		});

		return (
			<div className='order-tasks-drawer'>
				<DrawerV6 isWide={true} title='Order Tasks' onCancelClick={this.props.onCancel} onSaveClick={this.onSave}>
			<LoadingIndicator isLoading={this.state.isLoading}>
				<div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
					<DragDropContext onDragEnd={this.onDragEnd}>
						<Droppable droppableId='droppable'>
								{(provided, snapshot) => (
									<div ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)}>
										{this.state.tasks.map((task, index) => (
											<Draggable key={task.id} draggableId={task.id.toString()} index={index}>
												{(provided, snapshot) => (
													<div
														className='task row'
														ref={provided.innerRef}
														{...provided.draggableProps}
														{...provided.dragHandleProps}
														style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}>
														<div className='col-1'>
															<div className='bars' style={{ color: '#6860E4' }}>
																<Icon name='bars' />
															</div>
														</div>
														<div className='col-11 task-name pt-2' style={{ color: '#6860E4' }}>
															{task.name}
														</div>
													</div>
												)}
											</Draggable>
										))}
										{provided.placeholder}
									</div>
								)}
							</Droppable>
						</DragDropContext>
					</div>
				</LoadingIndicator>
				</DrawerV6>
			</div>
		);
	}
}

OrderTasksDrawer.propTypes = {
	subscriptionId: PropTypes.string.isRequired,
	baselineId: PropTypes.string.isRequired,
	targetDaysOut: PropTypes.string.isRequired,
	targetStaticDate: PropTypes.string.isRequired,
	onComplete: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
	baselineTaskActions: PropTypes.object.isRequired,
};

const mapDispatchToProps = (dispatch) => {
	return {
		baselineTaskActions: bindActionCreators(baselineTaskActions, dispatch),
	};
};

export default connect(null, mapDispatchToProps)(OrderTasksDrawer);
